import {
  faBrakeWarning,
  faCircleCheck,
  faDiamondExclamation,
  faHeadSideBrain,
} from '@fortawesome/duotone-regular-svg-icons';
import {
  faExclamationTriangle,
  faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type { SxProps } from '@mui/material/styles';

import { ALERT_SEVERITY } from '~/common/constants/api-ids';
import theme from '~/theme/mui/index';

type AlertProps = {
  message: React.ReactNode;
  severity:
    | 'info'
    | 'warning'
    | 'success'
    | 'error'
    | 'manualreview'
    | 'fadeAlert'
    | 'extractionFailed'
    | number;
  sx?: SxProps;
};

const getColor = (colorKey: string) => {
  // EX: Split 'blue.500' into 'blue' and '500'
  if (!colorKey || typeof colorKey !== 'string') return colorKey || 'black';
  const [color, shade] = colorKey.split('.');
  return theme.palette[color]?.[shade] || colorKey;
};

export default function Notice({
  message,
  severity,
  sx,
}: Readonly<AlertProps>) {
  let alertColor: string,
    alertIcon: any,
    alertBackground: string,
    alertIconColor: string,
    borderColor: string;

  switch (severity) {
    case 2:
    case 'warning':
      alertColor = 'uwmOrange.500';
      alertIconColor = alertColor;
      alertBackground = 'orange.50';
      alertIcon = faDiamondExclamation;
      borderColor = alertColor;
      break;
    case 'error':
      alertColor = 'red.600';
      alertIconColor = alertColor;
      alertBackground = 'red.50';
      alertIcon = faBrakeWarning;
      borderColor = alertColor;
      break;
    case 'success':
      alertColor = 'green.600';
      alertIconColor = alertColor;
      alertBackground = 'green.50';
      alertIcon = faCircleCheck;
      borderColor = alertColor;
      break;
    case 'extractionFailed':
    case ALERT_SEVERITY.ATTENTION:
    case 'manualreview':
      alertBackground = 'orange.300';
      alertIcon = faFlag;
      alertIconColor = 'black';
      borderColor = 'orange.900';
      break;
    case 'fadeAlert':
      alertBackground = 'gray.200';
      alertIcon = faExclamationTriangle;
      borderColor = 'gray.900';
      break;
    default:
      alertColor = 'blue.500';
      alertIconColor = alertColor;
      alertBackground = 'blue.50';
      alertIcon = faHeadSideBrain;
      borderColor = alertColor;
      break;
  }
  return (
    <Alert
      icon={
        <FontAwesomeIcon
          icon={alertIcon}
          style={{
            color: getColor(alertIconColor) || 'black',
            boxSizing: 'content-box',
            maxWidth: '20px',
            maxHeight: '20px',
            minWidth: '20px',
            minHeight: '20px',
            padding: 'unset',
          }}
        />
      }
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        {
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px 4px 4px',
          borderRadius: '4px',
          borderLeft: '6px solid',
          borderColor: borderColor,
          backgroundColor: alertBackground,
          color: 'gray.900',
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: '1rem',
          width: 'fit-content',
          minWidth: 0,
          '& .MuiAlert-icon': {
            padding: 'unset',
          },
          '& .MuiAlert-message': {
            padding: 'unset',
          },
        },
      ]}
    >
      <Box component="span" sx={{ fontSize: 'sm', lineHeight: '1.5rem' }}>
        {message}
      </Box>
    </Alert>
  );
}
